import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Sparkles, ChevronLeft } from 'lucide-react';
import { PomodoroTimer } from './components/PomodoroTimer';
// --- Data ---
const TOOLS = [{
  id: 'word-counter',
  name: 'Word Counter',
  emoji: '📝',
  lastUsed: 'Just now'
}, {
  id: 'tip-calculator',
  name: 'Tip Calculator',
  emoji: '💵',
  lastUsed: '2h ago'
}, {
  id: 'pomodoro',
  name: 'Pomodoro Timer',
  emoji: '🍅',
  lastUsed: 'Yesterday'
}, {
  id: 'qr-generator',
  name: 'QR Generator',
  emoji: '🔳',
  lastUsed: '3d ago'
}, {
  id: 'unit-converter',
  name: 'Unit Converter',
  emoji: '📏',
  lastUsed: '1w ago'
}, {
  id: 'password-generator',
  name: 'Password Generator',
  emoji: '🔐',
  lastUsed: 'Never'
}, {
  id: 'habit-tracker',
  name: 'Habit Tracker',
  emoji: '✅',
  lastUsed: 'Just now'
}, {
  id: 'random-picker',
  name: 'Random Picker',
  emoji: '🎲',
  lastUsed: '1mo ago'
}, {
  id: 'color-picker',
  name: 'Color Picker',
  emoji: '🎨',
  lastUsed: 'Never'
}, {
  id: 'invoice-generator',
  name: 'Invoice Generator',
  emoji: '🧾',
  lastUsed: '2mo ago'
}, {
  id: 'bmi-calculator',
  name: 'BMI Calculator',
  emoji: '⚖️',
  lastUsed: 'Never'
}, {
  id: 'budget-splitter',
  name: 'Budget Splitter',
  emoji: '💸',
  lastUsed: '5d ago'
}];
// --- Components ---
function PlaceholderTool({
  tool,
  onClose
}: {
  tool: (typeof TOOLS)[0];
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} className="min-h-screen w-full bg-app-bg flex flex-col items-center justify-center p-6" data-id="element-0">
      <button onClick={onClose} className="absolute top-6 left-6 flex items-center gap-2 text-neutral-400 hover:text-neutral-100 transition-colors group" data-id="element-1">
        <div className="w-8 h-8 rounded-full bg-app-surface border border-app-border flex items-center justify-center group-hover:border-neutral-700 transition-colors" data-id="element-2">
          <ChevronLeft size={18} data-id="element-3" />
        </div>
        <span className="font-medium text-sm" data-id="element-4">Back</span>
      </button>

      <div className="text-8xl mb-6" data-id="element-5">{tool.emoji}</div>
      <h2 className="text-3xl font-semibold text-neutral-100 mb-4" data-id="element-6">
        {tool.name}
      </h2>
      <p className="text-neutral-500" data-id="element-7">
        This tool is not fully implemented in this demo.
      </p>
    </motion.div>;
}
export function App() {
  const [activeToolId, setActiveToolId] = useState<string | null>('pomodoro');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredTools = useMemo(() => {
    if (!searchQuery.trim()) return TOOLS;
    const query = searchQuery.toLowerCase();
    return TOOLS.filter(tool => tool.name.toLowerCase().includes(query));
  }, [searchQuery]);
  const activeTool = useMemo(() => TOOLS.find(t => t.id === activeToolId), [activeToolId]);
  return <div className="min-h-screen bg-app-bg text-neutral-100 font-sans selection:bg-app-accent selection:text-app-bg overflow-x-hidden" data-id="element-8">
      <AnimatePresence mode="wait" data-id="element-9">
        {activeToolId ? <motion.div key="tool-view" className="absolute inset-0 z-10 bg-app-bg" data-id="element-10">
            {activeToolId === 'pomodoro' ? <PomodoroTimer onClose={() => setActiveToolId(null)} data-id="element-11" /> : activeTool ? <PlaceholderTool tool={activeTool} onClose={() => setActiveToolId(null)} data-id="element-12" /> : null}
          </motion.div> : <motion.div key="grid-view" initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="max-w-[1200px] mx-auto px-6 py-12 md:py-20" data-id="element-13">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12" data-id="element-14">
              <div data-id="element-15">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-3" data-id="element-16">
                  SimpleTools
                </h1>
                <p className="text-neutral-500 mt-2 font-medium flex items-center gap-2 text-sm" data-id="element-17">
                  12 tools{' '}
                  <span className="w-1 h-1 rounded-full bg-neutral-700" data-id="element-18" /> zero
                  ads <span className="w-1 h-1 rounded-full bg-neutral-700" data-id="element-19" />{' '}
                  always
                </p>
              </div>
              <div className="inline-flex items-center gap-2 bg-app-accent-soft text-app-accent px-4 py-2 rounded-full text-sm font-medium border border-app-accent/20 shadow-[0_0_15px_rgba(251,113,133,0.1)]" data-id="element-20">
                <Sparkles size={16} data-id="element-21" />
                <span data-id="element-22">New tool every week</span>
              </div>
            </header>

            {/* Search */}
            <div className="relative mb-12 group" data-id="element-23">
              <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none text-neutral-500 group-focus-within:text-app-accent transition-colors" data-id="element-24">
                <Search size={22} data-id="element-25" />
              </div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Find a tool..." className="w-full bg-app-surface border border-app-border rounded-2xl py-5 pl-14 pr-6 text-lg text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:border-app-accent focus:ring-1 focus:ring-app-accent transition-all shadow-sm" data-id="element-26" />
            </div>

            {/* Grid */}
            <motion.div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" layout data-id="element-27">
              <AnimatePresence data-id="element-28">
                {filteredTools.map((tool, index) => <motion.button key={tool.id} layout initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.2,
              delay: index * 0.03
            }} onClick={() => setActiveToolId(tool.id)} className="group relative flex flex-col items-start p-6 bg-app-surface rounded-2xl border border-app-border hover:border-app-accent/50 hover:-translate-y-1 transition-all duration-300 text-left w-full focus:outline-none focus:ring-2 focus:ring-app-accent focus:ring-offset-2 focus:ring-offset-app-bg" data-id="element-29">
                    <div className="w-14 h-14 rounded-xl bg-app-accent-soft flex items-center justify-center text-3xl mb-5 group-hover:scale-110 transition-transform duration-300 ease-out" data-id="element-30">
                      {tool.emoji}
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-100 mb-1" data-id="element-31">
                      {tool.name}
                    </h3>
                    <p className="text-sm text-neutral-500 font-medium" data-id="element-32">
                      {tool.lastUsed === 'Never' ? 'Never used' : `Used ${tool.lastUsed.toLowerCase()}`}
                    </p>
                  </motion.button>)}
              </AnimatePresence>

              {filteredTools.length === 0 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="col-span-full py-20 text-center text-neutral-500" data-id="element-33">
                  <Search size={48} className="mx-auto mb-4 opacity-20" data-id="element-34" />
                  <p className="text-lg" data-id="element-35">
                    No tools found matching "{searchQuery}"
                  </p>
                </motion.div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}